/* global BigInt */
/**
 * ID Encoder / Decoder Utility
 * Uses arithmetic obfuscation and Base62 encoding to generate long, hash-like IDs.
 */

const ALPHABET = 'qE1lWzM2tTyYuUiIoOpPaAsSdDfFgGhHjJkKCvZxXcVbBnN3456789QweRr0mL';
const BASE = BigInt(ALPHABET.length);

// Constants for Obfuscation (Do not change these after deployment!)
// Using BigInt to safely handle larger numbers
const PRIME = 982451653n;
const XOR_KEY = 123456789n;

/**
 * Encodes an integer ID into a long, complex string.
 * Example: 1 -> 'x9J2kL3m' (approx)
 * @param {number|string} id - The integer ID to encode
 * @returns {string} The encoded string
 */
export const encodeId = (id) => {
    let num = parseInt(id, 10);
    if (isNaN(num) || num === null || num === undefined) return '';

    // Step 1: Arithmetic Obfuscation (transform small ID to huge number)
    // Formula: (ID * PRIME) XOR KEY
    let bigNum = (BigInt(num) * PRIME) ^ XOR_KEY;

    // Step 2: Base62 Encoding
    if (bigNum === 0n) return ALPHABET[0];

    let encoded = '';
    while (bigNum > 0n) {
        let remainder = bigNum % BASE;
        encoded = ALPHABET[Number(remainder)] + encoded;
        bigNum = bigNum / BASE; // BigInt division automatically floors the result
    }

    return encoded;
};

/**
 * Decodes an encoded string back to an integer ID.
 * Falls back to returning the input as an integer if it's already numeric.
 * @param {string} str - The encoded string to decode
 * @returns {number|null} The original integer ID, or null if invalid
 */
export const decodeId = (str) => {
    if (!str) return null;

    // Backward Compatibility: If string is purely numeric, treat as raw ID
    // Note: Our encoded strings might contain numbers, but usually mixed with letters.
    // If you want strict enforcement, remove this check.
    if (/^\d+$/.test(str)) {
        return parseInt(str, 10);
    }

    try {
        // Step 1: Base62 Decoding
        let bigNum = 0n;
        for (let i = 0; i < str.length; i++) {
            const char = str[i];
            const index = ALPHABET.indexOf(char);

            if (index === -1) {
                console.warn(`Invalid character '${char}' in ID code '${str}'`);
                return null;
            }

            bigNum = bigNum * BASE + BigInt(index);
        }

        // Step 2: Reverse Arithmetic Obfuscation
        // Formula: (Obfuscated XOR KEY) / PRIME
        const originalBigInt = (bigNum ^ XOR_KEY) / PRIME;

        return Number(originalBigInt);
    } catch (err) {
        console.error('Error decoding ID:', err);
        return null;
    }
};
